import { useState, useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { TRADES, calcsForTrade } from '@/lib/calculators';
import CalculatorCard from '@/components/CalculatorCard';
import OverheadsCard from '@/components/OverheadsCard';
import { ArrowLeft, ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import Seo from '@/components/Seo';

export default function TradePage() {
  const { slug } = useParams();
  const trade = TRADES.find((t) => t.slug === slug);
  const calcs = [...calcsForTrade(slug)].sort((a, b) => b.inputs.length - a.inputs.length);
  const [openMap, setOpenMap] = useState({});

  useEffect(() => {
    const m = {};
    calcs.forEach((c) => { m[c.slug] = false; });
    setOpenMap(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const allOpen = calcs.length > 0 && calcs.every((c) => openMap[c.slug] === true);
  const setAll = (val) => {
    const m = {};
    calcs.forEach((c) => { m[c.slug] = val; });
    setOpenMap(m);
  };
  const toggleOne = (s) => setOpenMap((prev) => ({ ...prev, [s]: prev[s] !== true }));

  if (!trade) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <h1 className="font-display font-semibold text-3xl uppercase">Trade not found</h1>
        <Link to="/trades" className="tm-btn mt-6">Back to trades</Link>
      </div>
    );
  }
  const isBusiness = slug === 'business-essentials';
  const SITE = 'https://tradesmateuk.co.uk';
  const jsonLd = [
    {
      '@context': 'https://schema.org', '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Trades', item: `${SITE}/trades` },
        { '@type': 'ListItem', position: 2, name: trade.name, item: `${SITE}/trades/${slug}` },
      ],
    },
    {
      '@context': 'https://schema.org', '@type': 'CollectionPage',
      name: `${trade.name} Calculators`,
      url: `${SITE}/trades/${slug}`,
      description: `Free ${trade.name.toLowerCase()} calculators for UK tradesmen.`,
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: calcs.slice(0, 25).map((c, i) => ({
          '@type': 'ListItem', position: i + 1, name: c.name,
          url: `${SITE}/calculators/${c.slug}`,
        })),
      },
    },
  ];
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo
        title={`${trade.name} Calculators`}
        description={`Free ${trade.name.toLowerCase()} calculators for UK tradesmen — ${calcs.slice(0, 4).map((c) => c.name).join(', ')} and more. Instant results, no sign-up.`}
        path={`/trades/${slug}`}
        jsonLd={jsonLd}
      />
      <Link to="/trades" data-testid={`trade-back-${slug}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-600 hover:text-brand mb-4 font-semibold">
        <ArrowLeft size={14}/> All trades
      </Link>
      <header className="mb-10 flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-brand font-semibold">{trade.tagline}</p>
          <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1">{trade.name}</h1>
          <p className="text-slate-600 mt-2">{calcs.length + (isBusiness ? 1 : 0)} calculator{calcs.length + (isBusiness ? 1 : 0) !== 1 ? 's' : ''} available.</p>
        </div>
        <Link to="/estimator" className="tm-btn">Open estimator →</Link>
      </header>
      {calcs.length > 0 && (
        <div className="flex justify-end mb-4">
          <button
            type="button"
            data-testid="trade-toggle-all"
            onClick={() => setAll(!allOpen)}
            className="tm-btn tm-btn-ghost text-xs py-2 min-h-0 inline-flex items-center gap-2"
          >
            {allOpen ? <ChevronsDownUp size={14}/> : <ChevronsUpDown size={14}/>}
            {allOpen ? 'Collapse all' : 'Expand all'}
          </button>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {isBusiness && <OverheadsCard />}
        {calcs.map((c) => (
          <CalculatorCard
            key={c.slug}
            calc={c}
            open={openMap[c.slug] === true}
            onToggle={() => toggleOne(c.slug)}
          />
        ))}
      </div>
      {calcs.length === 0 && !isBusiness && (
        <p className="font-mono text-slate-500 mt-6">More calculators coming soon for this trade.</p>
      )}
    </div>
  );
}

// Redirect /overheads to /trades/business-essentials for old links/bookmarks
export function OverheadsRedirect() {
  return <Navigate to="/trades/business-essentials" replace />;
}
